import React, { type CSSProperties } from "react";
import { observer } from "mobx-react";
import { IRichToolTipShowOptions, IRichToolTipStore, isDangerousHtml } from "./RichToolTipStore";
import { getBoundingClientRect } from "../../util/functions/getBoundingClientRect";
import { injectFromApp } from "../inject";
import { delay } from "../../util/functions/delay";
import type { IPointInWindow } from "../../viewer/rendering/Point";
import { DocumentBodyPortalComponent } from "../../util/components/DocumentBodyPortalComponent";

export interface IRichToolTipBaseComponentProps {
    richToolTipStore?: IRichToolTipStore;
}

@injectFromApp<IRichToolTipBaseComponentProps>((stores) => ({
    richToolTipStore: stores.richToolTipStore,
}))

@observer
export class RichToolTipBaseComponent extends React.Component<IRichToolTipBaseComponentProps> {
    private readonly tooltipDiv: React.RefObject<HTMLDivElement>;

    constructor(props: IRichToolTipBaseComponentProps) {
        super(props);
        this.tooltipDiv = React.createRef<HTMLDivElement>();
    }

    async componentDidUpdate(prevProps: Readonly<IRichToolTipBaseComponentProps>, prevState: Readonly<{}>, snapshot?: any) {
        const { richToolTipStore } = this.props;
        const { current } = this.tooltipDiv;
        if (!current) return;
        const tooltipRect = getBoundingClientRect(current);
        await delay(0);
        setTimeout(() => richToolTipStore!.toolTipComponentDidUpdate(tooltipRect), 0);
    }

    private readonly getRichToolTipStyle = (richToolTipLocation: IPointInWindow, richToolTipShowOptions: IRichToolTipShowOptions | null): CSSProperties => {
        const left = richToolTipLocation.x + "px";
        const top = richToolTipLocation.y + "px";
        let width: string | undefined;
        let height: string | undefined;
        let maxWidth: string | undefined;
        let maxHeight: string | undefined;
        let minWidth: string | undefined;
        let minHeight: string | undefined;
        let zIndex: number | undefined;
        if (richToolTipShowOptions) {
            const { size, maxSize, minSize, zIndex: zIndexValue } = richToolTipShowOptions;
            if (size) {
                width = size.width > 0 ? `${ size.width }px` : undefined;
                height = size.height > 0 ? `${ size.height }px` : undefined;
            }
            if (maxSize) {
                maxWidth = maxSize.width > 0 ? `${ maxSize.width }px` : undefined;
                maxHeight = maxSize.height > 0 ? `${ maxSize.height }px` : undefined;
            }
            if (minSize) {
                minWidth = minSize.width > 0 ? `${ minSize.width }px` : undefined;
                minHeight = minSize.height > 0 ? `${ minSize.height }px` : undefined;
            }
            if (zIndexValue) {
                zIndex = zIndexValue;
            }
        }

        return {
            left,
            top,
            width,
            height,
            maxWidth,
            maxHeight,
            minWidth,
            minHeight,
            zIndex,
        };
    };

    render() {
        const { richToolTipStore } = this.props;
        const { richToolTipContent, richToolTipLocation, richToolTipShowOptions, isVisible } = richToolTipStore!;

        if (!isVisible || !richToolTipLocation) {
            return null;
        }

        const content = typeof richToolTipContent === "function" ? richToolTipContent() : richToolTipContent;
        const style = this.getRichToolTipStyle(richToolTipLocation, richToolTipShowOptions);

        if (content === null)
            return null;

        return (
            <DocumentBodyPortalComponent document={ document } key={ "rich-tool-tip-component" }>
                { isDangerousHtml(content) ?
                    <div ref={ this.tooltipDiv } className={ `rich-tooltip ${ richToolTipShowOptions?.className }` }
                         style={ style }
                         dangerouslySetInnerHTML={ content } /> :
                    <div ref={ this.tooltipDiv } className={ `rich-tooltip ${ richToolTipShowOptions?.className }` }
                         style={ style }>
                        { content }
                    </div>
                }
            </DocumentBodyPortalComponent>
        );
    }
}
